import {
  EAppSupportType,
  EPropertyType,
  IComponent,
  IProperty,
  IPropertyTypeOptions,
  ISymbol
} from "@kuika/kuika-cl-model";
import React from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import KuikaButton from "../../../ui-kit/kuika-button/kuika-button";
import KuikaEmptyState from "../../../ui-kit/kuika-empty-state/kuika-empty-state";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTooltip from "../../../ui-kit/kuika-tooltip/kuika-tooltip";
import KuikaSmallMediumText from "../../../ui-kit/typography/SmallMediumText/SmallMediumText";
import KuikaSmallText from "../../../ui-kit/typography/SmallText/SmallText";
import { ComponentPropertiesModal } from "./component-properties-modal";
import "./component-def-second-step.scss";

export interface IComponentFormSecondStepState {
  enumKey: string;
  enumValue: string;
  isComponentPropertyModalActive: boolean;
  idx: number;
}

export interface IComponentFormSecondStepProps {
  properties: IProperty[];
  component: IComponent;
  getPropertiesInfo: any;
}

/** The options-bag role flags `getPropertyRoleLabel` checks on a property, keyed dynamically. */
interface IPropertyRoleFlags {
  isOptionsProp?: boolean;
  isLanguageOptionsProp?: boolean;
  isGoogleMapsOptionsProp?: boolean;
  isTableOptionsProp?: boolean;
  isChatOptionsProp?: boolean;
  isTreeOptionsProp?: boolean;
  isChartOptionsProp?: boolean;
  isTimelineOptionsProp?: boolean;
  isCalendarOptionsProp?: boolean;
  isMenuOptionsProp?: boolean;
  isBreadcrumbOptionsProp?: boolean;
  isRadialChartOptionsProp?: boolean;
  isRadialChartLevelOptionsProp?: boolean;
  isMixedChartOptionsProp?: boolean;
  isMixedChartLineOptionsProp?: boolean;
  isMixedChartPointOptionsProp?: boolean;
  isMixedChartBarOptionsProp?: boolean;
  isKanbanBoardLaneOptionsProp?: boolean;
  isKanbanBoardCardOptionsProp?: boolean;
  isNodeTreeNodeOptionsProp?: boolean;
  isDirectoryOptionsProp?: boolean;
}

const reorderProperties = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result as IProperty[];
};

class ComponentFormSecondStep extends React.PureComponent<
  IComponentFormSecondStepProps & IWithContextProp & IWithSDProps,
  IComponentFormSecondStepState
> {
  constructor(props: IComponentFormSecondStepProps & IWithContextProp & IWithSDProps) {
    super(props);
    this.state = {
      enumKey: "",
      enumValue: "",
      isComponentPropertyModalActive: false,
      idx: 0
    };
  }

  submitProperties = () => {
    this.props.getPropertiesInfo(this.props.properties);
  };

  refactorProperties = (properties: IProperty[]) => {
    const updatedProperties = properties;
    updatedProperties.forEach((p) => {
      const currentTypeOptions: IPropertyTypeOptions[] = [];
      p.type.forEach((t) => {
        let selectedType: any = null;
        if (p && p.typeOptions && KMainFunctions.isObjectEmpty(p.typeOptions) === false && p.typeOptions.find) {
          selectedType = p.typeOptions.find((c) => Number(c.propertyType) === t);
          if (selectedType) {
            currentTypeOptions.push(selectedType);
          }
        } else {
          let hasOp = false;
          const hasOwnJSONSchema = false;
          let op: any[] = [];
          if (p.hasOptions === true) {
            hasOp = true;
          }
          if (p.options && p.options.length > 0) {
            op = p.options;
          }
          currentTypeOptions.push({
            propertyType: t.toString(),
            hasOptions: hasOp,
            options: op,
            hasOwnJSONSchema
          });
        }
        // p.typeOptions = currentTypeOptions;
        // const selectedP = p.typeOptions.find((c) => Number(c.propertyType) === p.selectedType);
        // if (selectedP) {
        //   p.options = selectedP.options;
        //   p.hasOptions = selectedP.hasOptions;
        // }
      });

      p.typeOptions = currentTypeOptions;
      const selectedP = p.typeOptions.find((c) => Number(c.propertyType) === p.selectedType);
      if (selectedP) {
        p.options = selectedP.options;
        p.hasOptions = selectedP.hasOptions;
      }
    });
    return updatedProperties;
  };

  componentDidUpdate = (prevProps: IComponentFormSecondStepProps & IWithContextProp) => {
    if (this.props && this.props !== prevProps) {
      if (
        this.props.component &&
        this.props.component.properties &&
        (!this.props.properties ||
          this.props.properties.length === 0 ||
          JSON.stringify(this.props.properties) === JSON.stringify(this.props.component.properties))
      ) {
        const updatedProperties = this.refactorProperties(this.props.component.properties);
        this.props.getPropertiesInfo(updatedProperties);
      }
    }
  };

  addProperty() {
    this.props.getPropertiesInfo([
      ...this.props.properties,
      {
        id: KMainFunctions.generateDisplayItemId(this.props.sd.screenDesignDictionary),
        name: "",
        symbol: {} as ISymbol,
        parameterName: "",
        type: [],
        options: [],
        hasOptions: false,
        description: "",
        selectedType: EPropertyType.string,
        typeOptions: [],
        isRequired: false,
        isTechnical: false,
        // isEvent: false,
        tagName: "",
        isAdvanced: false,
        isIgnoredOnCodeOutput: false,
        isIgnoredOnGeneratedApp: false,
        isSupportMultiLanguage: false,
        appSupportType: EAppSupportType.All,
        isDeprecated: false,
        isVisibleInFieldPicker: false,
        isValueProp: false,
        isOptionsProp: false,
        isLanguageOptionsProp: false,
        isTableOptionsProp: false,
        isEditabilityProp: false,
        isVisibleProp: false,
        isGoogleMapsOptionsProp: false,
        isKanbanBoardLaneOptionsProp: false,
        isKanbanBoardCardOptionsProp: false,
        isNodeTreeNodeOptionsProp: false,
        isDirectoryOptionsProp: false
      }
    ]);
  }

  removeProperty(i) {
    if (this.props.properties.length > 0) {
      const properties = [...this.props.properties];
      properties.splice(i, 1);
      this.props.getPropertiesInfo(properties);
    }
  }

  onDragEnd = (result) => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }
    const properties = reorderProperties(this.props.properties, result.source.index, result.destination.index);
    this.props.getPropertiesInfo(properties);
  };

  openComponentPropertyModal = async (idx: number) => {
    this.setState({ isComponentPropertyModalActive: true, idx });
  };

  closeComponentPropertyModal = () => {
    this.setState({ isComponentPropertyModalActive: false });
  };

  triggerPropertiesSetState = (val: IProperty[]) => {
    this.props.getPropertiesInfo(val);
  };

  handlePropertyType = (val: EPropertyType) => {
    if (val === EPropertyType.string) {
      return "String";
    }
    if (val === EPropertyType.identifier) {
      return "Identifier";
    }
    if (val === EPropertyType.event) {
      return "Event";
    }
    if (val === EPropertyType.column) {
      return "Column";
    }
    if (val === EPropertyType.object) {
      return "Object";
    }
    if (val === EPropertyType.enum) {
      return "Enum";
    }
    if (val === EPropertyType.number) {
      return "Number";
    }
    if (val === EPropertyType.boolean) {
      return "Boolean";
    }
    // else if (val === EPropertyType.stringArray) {
    //   return "StringArray";
    // } else if (val === EPropertyType.numberArray) {
    //   return "NumberArray";
    // }
    if (val === EPropertyType.ngDataInput) {
      return "ngDataInput";
    }
    if (val === EPropertyType.onlyAttributeName) {
      return "OnlyAttributeName";
    }
    if (val === EPropertyType.angularDataBind) {
      return "angularDataBind";
    }
    if (val === EPropertyType.image) {
      return "Image";
    }
    if (val === EPropertyType.icon) {
      return "Icon";
    }
    if (val === EPropertyType.dateTime) {
      return "DateTime";
    }
  };

  /** The options-bag role shown as a chip, mirroring the property modal's Data role. */
  getPropertyRoleLabel = (p: IProperty): string | undefined => {
    const roles: Record<string, string> = {
      isOptionsProp: "Options",
      isLanguageOptionsProp: "Language options",
      isGoogleMapsOptionsProp: "Maps options",
      isTableOptionsProp: "Table options",
      isChatOptionsProp: "Chat options",
      isTreeOptionsProp: "Tree options",
      isChartOptionsProp: "Chart options",
      isTimelineOptionsProp: "Timeline options",
      isCalendarOptionsProp: "Calendar options",
      isMenuOptionsProp: "Menu options",
      isBreadcrumbOptionsProp: "Breadcrumb options",
      isRadialChartOptionsProp: "Radial chart options",
      isRadialChartLevelOptionsProp: "Radial chart level options",
      isMixedChartOptionsProp: "Mixed chart options",
      isMixedChartLineOptionsProp: "Mixed chart line options",
      isMixedChartPointOptionsProp: "Mixed chart point options",
      isMixedChartBarOptionsProp: "Mixed chart bar options",
      isKanbanBoardLaneOptionsProp: "Kanban lane options",
      isKanbanBoardCardOptionsProp: "Kanban card options",
      isNodeTreeNodeOptionsProp: "Node tree options",
      isDirectoryOptionsProp: "Directory options"
    };
    const source: IPropertyRoleFlags = p as IPropertyRoleFlags;
    const hit: string | undefined = Object.keys(roles).find(
      (key: string) => source[key as keyof IPropertyRoleFlags] === true
    );
    if (hit) return roles[hit];
    if (p.isValueProp === true) return "Value";
    return undefined;
  };

  render() {
    return (
      <>
        {this.state.isComponentPropertyModalActive && (
          <ComponentPropertiesModal
            properties={this.props.properties}
            p={this.props.properties[this.state.idx]}
            idx={this.state.idx}
            showModal={this.state.isComponentPropertyModalActive}
            closeModal={this.closeComponentPropertyModal}
            triggerPropertiesSetState={this.triggerPropertiesSetState}
          />
        )}
        <div className="kuika-property-list" data-testid="componentPropertiesStep">
          <div className="kuika-property-list__toolbar">
            <div className="kuika-property-list__count">
              <KuikaSmallText>
                {`${this.props.properties?.length ?? 0} ${
                  (this.props.properties?.length ?? 0) === 1 ? "property" : "properties"
                }`}
              </KuikaSmallText>
            </div>
            <KuikaButton data-testid="componentPropertyAddButton" type="primary" onClick={this.addProperty.bind(this)}>
              Add Property
            </KuikaButton>
          </div>

          {(this.props.properties?.length ?? 0) === 0 ? (
            <KuikaEmptyState
              svgName="components"
              customName="No properties yet"
              description="Add a property to describe an attribute designers can set on this component."
            />
          ) : (
            <DragDropContext onDragEnd={this.onDragEnd}>
              <Droppable droppableId="droppable">
                {(provided) => (
                  <div className="kuika-property-list__rows" {...provided.droppableProps} ref={provided.innerRef}>
                    {this.props.properties?.map((p, idx) => {
                      const types: string[] = (p.type ?? [])
                        .map((item): string => this.handlePropertyType(item) ?? "")
                        .filter((label: string): boolean => label.length > 0);
                      const role: string | undefined = this.getPropertyRoleLabel(p);
                      return (
                        <Draggable key={idx} draggableId={`draggable_${idx}`} index={idx}>
                          {(provided, snapshot) => (
                            <div
                              className={`kuika-property-list__row${
                                snapshot.isDragging ? " kuika-property-list__row--dragging" : ""
                              }`}
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              data-testid={`componentPropertyRow-${idx}`}
                              data-property-name={p.name}
                              data-hidden-in-designer={p.isTechnical === true}
                              onClick={() => this.openComponentPropertyModal(idx)}
                            >
                              {/* The handle owns the drag, not the row: the row's own
                                  click opens the property, and a row-wide drag source
                                  swallows it. */}
                              <span
                                className="kuika-property-list__handle"
                                data-testid={`componentPropertyDragHandle-${idx}`}
                                aria-label={`Reorder ${p.name}`}
                                onClick={(e) => e.stopPropagation()}
                                {...provided.dragHandleProps}
                              >
                                <KuikaSvg name="grip-vertical" width={16} height={16} color="var(--dark-5)" />
                              </span>

                              <span className="kuika-property-list__index">
                                <KuikaSmallText>{idx + 1}</KuikaSmallText>
                              </span>

                              <span className="kuika-property-list__main">
                                <span className="kuika-property-list__name">
                                  <KuikaSmallMediumText>{p.name}</KuikaSmallMediumText>
                                  {p.isRequired && (
                                    <span className="kuika-property-list__required" title="Required">
                                      *
                                    </span>
                                  )}
                                </span>
                                {p.tagName && p.tagName !== p.name && (
                                  <KuikaSmallText className="kuika-property-list__alias">{p.tagName}</KuikaSmallText>
                                )}
                              </span>

                              <span className="kuika-property-list__types">
                                {types.map((label: string) => (
                                  <span className="kuika-property-list__type" key={label}>
                                    <KuikaSmallText>{label}</KuikaSmallText>
                                  </span>
                                ))}
                              </span>

                              <span className="kuika-property-list__badges">
                                {role && (
                                  <span className="kuika-property-list__badge kuika-property-list__badge--role">
                                    <KuikaSmallText>{role}</KuikaSmallText>
                                  </span>
                                )}
                                {p.isDeprecated && (
                                  <span className="kuika-property-list__badge kuika-property-list__badge--muted">
                                    <KuikaSmallText>Deprecated</KuikaSmallText>
                                  </span>
                                )}
                              </span>

                              <span className="kuika-property-list__actions">
                                {p.isTechnical && (
                                  <KuikaTooltip content="Hidden from the designer's properties panel">
                                    <span
                                      className="kuika-property-list__flag"
                                      data-testid={`componentPropertyTechnicalIcon-${idx}`}
                                    >
                                      <KuikaSvg name="eye-off" width={16} height={16} color="var(--blue-5)" />
                                    </span>
                                  </KuikaTooltip>
                                )}
                                <KuikaButton
                                  data-testid={`componentPropertyDeleteButton-${idx}`}
                                  type="tertiary"
                                  color="danger"
                                  size="sm"
                                  isIcon
                                  aria-label={`Delete ${p.name}`}
                                  onClick={(e) => {
                                    this.removeProperty(idx);
                                    e?.stopPropagation();
                                  }}
                                >
                                  <KuikaSvg name="trash" width={16} height={16} color="currentColor" />
                                </KuikaButton>
                              </span>
                            </div>
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          )}
        </div>
      </>
    );
  }
}

const componentFormSecondStep = withContext<IComponentFormSecondStepProps>(
  withSD<IComponentFormSecondStepProps>(ComponentFormSecondStep)
);
export { componentFormSecondStep as ComponentFormSecondStep };
